import { useState, useEffect, createContext, useContext } from 'react'
import authService from '../lib/auth'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    setLoading(true)
    
    try {
      // Check if user is already logged in
      if (authService.isAuthenticated()) {
        const storedUser = authService.getUser()
        
        // Verify token is still valid by fetching current user
        const result = await authService.getCurrentUser()
        
        if (result.user) {
          setUser(result.user)
          setIsAuthenticated(true)
        } else {
          // Token expired or invalid
          setUser(null)
          setIsAuthenticated(false)
        }
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    setLoading(true)
    
    try {
      const result = await authService.signIn(email, password)
      
      if (result.data && !result.error) {
        setUser(result.data.user)
        setIsAuthenticated(true)
        return { success: true, user: result.data.user }
      } else {
        return { success: false, error: result.error?.message || 'Login failed' }
      }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const register = async (email, password, username) => {
    setLoading(true)
    
    try {
      const result = await authService.signUp(email, password, username)
      
      if (result.data && !result.error) {
        setUser(result.data.user)
        setIsAuthenticated(true)
        return { success: true, user: result.data.user }
      } else {
        return { success: false, error: result.error?.message || 'Registration failed' }
      }
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    setLoading(true)
    
    try {
      await authService.signOut()
      setUser(null)
      setIsAuthenticated(false)
      return { success: true }
    } catch (error) {
      console.error('Logout error:', error)
      // Even if logout fails, clear local state
      setUser(null)
      setIsAuthenticated(false)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    authService
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}